import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Brain, RefreshCw, Loader2, Clock, Heart, Sparkles, X } from 'lucide-react';
import { Badge } from './ui/badge';
import AnalyticsApiService, { ConversationInsights } from '../services/analyticsApi';
import { ConversationAnalysisPanel } from './analytics/ConversationAnalysisPanel';

interface AIAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: number;
  conversationName: string;
}

export function AIAnalysisModal({
  isOpen,
  onClose,
  conversationId,
  conversationName
}: AIAnalysisModalProps) {
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<ConversationInsights | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load insights when modal opens
  useEffect(() => {
    if (isOpen && conversationId) {
      loadInitialInsights();
    }
  }, [isOpen, conversationId]);

  const loadInitialInsights = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Start with quick insights
      const quickInsights = await AnalyticsApiService.getConversationInsights(
        conversationId,
        { depth: 'quick' }
      );
      setInsights(quickInsights);

      // Then load standard insights in background
      const standardInsights = await AnalyticsApiService.getConversationInsights(
        conversationId,
        { depth: 'standard' }
      );
      setInsights(standardInsights);
    } catch (err: any) {
      setError(err.message || 'Failed to load insights');
      console.error('Error loading AI insights:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);

    try {
      // Force refresh all insights
      const freshInsights = await AnalyticsApiService.getConversationInsights(
        conversationId,
        { depth: 'deep', force_refresh: true }
      );
      setInsights(freshInsights);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh insights');
      console.error('Error refreshing AI insights:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="!max-w-none w-[96vw] h-[92vh] p-0 overflow-hidden !bg-white dark:!bg-gray-900 border border-gray-200 dark:border-gray-700 flex flex-col [&>button]:hidden" 
        style={{ maxWidth: '96vw', width: '96vw' }}
      >
        {/* Compact Header */}
        <DialogHeader className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            {/* Left: Title & Stats */}
            <div className="flex items-center gap-6">
              <DialogTitle className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Brain className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex items-baseline gap-3">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    AI Analysis
                  </h2>
                  <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
                    {conversationName}
                  </DialogDescription>
                </div>
              </DialogTitle>

              {/* Quick Stats - Inline */}
              {insights && insights.quick_metrics && (
                <div className="flex items-center gap-3 pl-3 border-l border-gray-300 dark:border-gray-700">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Messages:</span>
                    <Badge variant="secondary" className="text-xs">{insights.quick_metrics.message_count}</Badge>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Participants:</span>
                    <Badge variant="secondary" className="text-xs">{insights.quick_metrics.participant_count}</Badge>
                  </div>
                  {insights.quick_metrics.sentiment_simple && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Sentiment:</span>
                      <Badge 
                        variant={
                          insights.quick_metrics.sentiment_simple === 'positive' ? 'default' :
                          insights.quick_metrics.sentiment_simple === 'negative' ? 'destructive' :
                          'secondary'
                        }
                        className="text-xs"
                      >
                        {insights.quick_metrics.sentiment_simple}
                      </Badge>
                    </div>
                  )}
                  {insights.quick_metrics.primary_emotion && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Emotion:</span>
                      <Badge variant="outline" className="capitalize text-xs">
                        {insights.quick_metrics.primary_emotion}
                      </Badge>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={loading}
                className="gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </>
                )}
              </Button>
              
              <Button
                onClick={onClose}
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Content - Full Width Horizontal Layout */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 mx-2">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {loading && !insights ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-purple-600" />
              <p className="text-gray-600 dark:text-gray-400">Loading AI insights...</p>
            </div>
          ) : insights ? (
            <ConversationAnalysisPanel
              conversationId={conversationId}
              conversationTitle=""
              className="shadow-none border-0 w-full !max-w-none"
              hideHeader={true}
              onRefresh={handleRefresh}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full space-y-4">
              <Brain className="h-16 w-16 text-gray-300 dark:text-gray-600" />
              <div className="text-center space-y-2">
                <p className="text-gray-600 dark:text-gray-400">No insights available yet</p>
                <Button onClick={loadInitialInsights} variant="outline">
                  Load AI Analysis
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

